import React, { useState, useEffect } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { Users, Calendar, CheckCircle2, AlertTriangle, Info, Search } from 'lucide-react';
import AILoadingOverlay from '../common/AILoadingOverlay';
import './ConfigureBackChecks.css';

// API Base URL - uses environment variable in production
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ConfigureBackChecks = ({ 
  backcheckData,
  primaryData,
  projectId,  // NEW: Project ID for server-side backcheck analysis (Phase 2)
  onNavigate,
  backcheckConfig,
  setBackcheckConfig,
  isLoadingBackcheck,
  setIsLoadingBackcheck,
  backcheckError,
  setBackcheckError,
  backcheckResults,
  setBackcheckResults,
  isExpandedBackcheck,
  setIsExpandedBackcheck
}) => {
  const [hoveredVariable, setHoveredVariable] = useState({ type: null, variable: null });
  const [expandedTypes, setExpandedTypes] = useState([]); // Track which cards are expanded
  const [duplicateVariables, setDuplicateVariables] = useState([]); // Track duplicate variables across types
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false); // Toggle advanced options panel
  const [variableSearch, setVariableSearch] = useState({}); // Search filter per variable type
  const { isDarkMode } = useTheme();
  
  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;

  const primaryColumns = primaryData?.columns || [];

  const getAllBackcheckVariables = () => {
    if (!backcheckData || !backcheckData.columns) return [];
    return backcheckData.columns;
  };

  const getBackcheckColumnTypes = () => {
    if (!backcheckData || !backcheckData.column_types) return {};
    return backcheckData.column_types;
  };

  const getDateTimeVariables = () => {
    const columnTypes = getBackcheckColumnTypes();
    return allVariables.filter(variable => {
      const colType = columnTypes[variable];
      return colType === 'timestamp' || 
             colType === 'datetime' || 
             colType === 'date' ||
             colType === 'datetime64';
    });
  };
  
  const allVariables = getAllBackcheckVariables();
  const columnTypes = getBackcheckColumnTypes();

  // Find duplicate variables across type selections
  const findDuplicateVariables = (config) => {
    const type1 = config.type1Variables || [];
    const type2 = config.type2Variables || [];
    const type3 = config.type3Variables || [];

    const duplicates = [];
    const allSelected = [...type1, ...type2, ...type3];

    // Count occurrences
    const counts = {};
    allSelected.forEach(variable => {
      counts[variable] = (counts[variable] || 0) + 1;
    });

    // Find variables that appear more than once
    Object.entries(counts).forEach(([variable, count]) => {
      if (count > 1) {
        // Determine which types contain this variable
        const inTypes = [];
        if (type1.includes(variable)) inTypes.push('Type 1');
        if (type2.includes(variable)) inTypes.push('Type 2');
        if (type3.includes(variable)) inTypes.push('Type 3');
        duplicates.push({ variable, types: inTypes });
      }
    });

    return duplicates;
  };

  // Check if a specific variable is a duplicate
  const isDuplicateVariable = (variableName) => {
    return duplicateVariables.some(d => d.variable === variableName);
  };

  // Check for duplicates whenever config changes
  useEffect(() => {
    const duplicates = findDuplicateVariables(backcheckConfig);
    setDuplicateVariables(duplicates);
  }, [backcheckConfig.type1Variables, backcheckConfig.type2Variables, backcheckConfig.type3Variables]);

  // Initialize expandedTypes based on which types have selections
  useEffect(() => {
    const typesWithSelections = [];
    if (backcheckConfig.type1Variables?.length > 0) typesWithSelections.push('type1');
    if (backcheckConfig.type2Variables?.length > 0) typesWithSelections.push('type2');
    if (backcheckConfig.type3Variables?.length > 0) typesWithSelections.push('type3');
    
    if (typesWithSelections.length > 0) {
      setExpandedTypes(typesWithSelections);
    }
  }, [backcheckConfig.type1Variables, backcheckConfig.type2Variables, backcheckConfig.type3Variables]);

  const handleConfigChange = (field, value) => {
    const newConfig = {
      ...backcheckConfig,
      [field]: value
    };
    
    setBackcheckConfig(newConfig);
    
    if (setBackcheckResults) {
      setBackcheckResults(null);
    }
    if (setBackcheckError) {
      setBackcheckError(null);
    }
  };

  const toggleVariableType = (type) => {
    const isExpanded = expandedTypes.includes(type);
    
    if (isExpanded) {
      // Collapse: remove from expanded list
      setExpandedTypes(prev => prev.filter(t => t !== type));
      
      // Also clear selections for this type
      const fieldName = `${type}Variables`;
      const newConfig = {
        ...backcheckConfig,
        [fieldName]: []
      };
      setBackcheckConfig(newConfig);
    } else {
      // Expand: add to expanded list
      setExpandedTypes(prev => [...prev, type]);
    }
  };

  const handleVariableSelection = (type, variableName) => {
    const fieldName = `${type}Variables`;
    const currentSelection = backcheckConfig[fieldName] || [];
    const isSelected = currentSelection.includes(variableName);
    
    const newConfig = {
      ...backcheckConfig,
      [fieldName]: isSelected
        ? currentSelection.filter(v => v !== variableName)
        : [...currentSelection, variableName]
    };
    
    setBackcheckConfig(newConfig);
  };

  // Select All variables for a type
  const selectAllVariables = (type, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    const fieldName = `${type}Variables`;
    const newConfig = {
      ...backcheckConfig,
      [fieldName]: [...allVariables]
    };
    
    setBackcheckConfig(newConfig);
    
    // Clear any previous results
    if (setBackcheckResults) {
      setBackcheckResults(null);
    }
  };

  // Clear all selected variables for a type
  const clearVariableSelection = (type, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    const fieldName = `${type}Variables`;
    const newConfig = {
      ...backcheckConfig,
      [fieldName]: []
    };
    
    setBackcheckConfig(newConfig);
    
    // Clear any previous results
    if (setBackcheckResults) {
      setBackcheckResults(null);
    }
  };

  // Select/deselect the Type 1 / Type 2 / Type 3 cards themselves (expand or
  // collapse all three at once). Mirrors toggleVariableType's per-card
  // behavior but applied to all three in one click. This is separate from
  // selectAllVariables/clearVariableSelection above, which only touch the
  // variables inside a single already-expanded card.
  const BACKCHECK_TYPES = ['type1', 'type2', 'type3'];

  const selectAllTypeCards = () => {
    const notExpanded = BACKCHECK_TYPES.filter(t => !expandedTypes.includes(t));
    if (notExpanded.length === 0) return;
    setExpandedTypes(prev => [...prev, ...notExpanded]);
  };

  const deselectAllTypeCards = () => {
    if (expandedTypes.length === 0) return;
    setExpandedTypes([]);
    const newConfig = { ...backcheckConfig };
    BACKCHECK_TYPES.forEach(t => { newConfig[`${t}Variables`] = []; });
    setBackcheckConfig(newConfig);
    if (setBackcheckResults) {
      setBackcheckResults(null);
    }
  };

  const validateConfiguration = () => {
    if (!backcheckConfig.primaryRespondentId || backcheckConfig.primaryRespondentId.trim() === '') {
      setBackcheckError?.('Please select a Primary Respondent ID');
      return false;
    }

    if (!backcheckConfig.backcheckRespondentId || backcheckConfig.backcheckRespondentId.trim() === '') {
      setBackcheckError?.('Please select a Backcheck Respondent ID');
      return false;
    }
    
    if (!backcheckConfig.backcheckDataCollectorId) {
      setBackcheckError?.('Please select a Back-check Data Collector ID');
      return false;
    }
    
    if (!backcheckConfig.backcheckInterviewDateId) {
      setBackcheckError?.('Please select a Back-check Interview Date');
      return false;
    }
    
    const totalVariables = [
      ...(backcheckConfig.type1Variables || []),
      ...(backcheckConfig.type2Variables || []),
      ...(backcheckConfig.type3Variables || [])
    ].length;
    
    if (totalVariables === 0) {
      setBackcheckError?.('Please select at least one variable for back-check analysis');
      return false;
    }
    
    // Check for duplicate variables across types
    const duplicates = findDuplicateVariables(backcheckConfig);
    if (duplicates.length > 0) {
      const duplicateList = duplicates
        .map(d => `"${d.variable}" (selected in ${d.types.join(' and ')})`)
        .join(', ');
      setBackcheckError?.(`Variables can only be selected under one type. Please remove duplicates: ${duplicateList}`);
      return false;
    }
    
    return true;
  };

  const runBackcheckAnalysis = async () => {
    if (!validateConfiguration()) return;
    
    setIsLoadingBackcheck?.(true);
    setBackcheckError?.(null);
    
    try {
      // Parse exclude numeric values
      const excludeNumeric = backcheckConfig.excludeNumeric
        ? backcheckConfig.excludeNumeric.split(',').map(v => v.trim()).filter(v => v !== '' && !isNaN(parseFloat(v))).map(v => parseFloat(v))
        : [];
      
      // Parse exclude string values
      const excludeString = backcheckConfig.excludeString
        ? backcheckConfig.excludeString.split(',').map(v => v.trim()).filter(v => v !== '')
        : [];
      
      // Parse tolerance ranges
      const toleranceRanges = (backcheckConfig.toleranceRanges || [])
        .filter(r => r.variable && (r.min || r.max))
        .map(r => ({
          variable: r.variable,
          min: r.min || '0',
          max: r.max || '0'
        }));

      const analysisConfig = {
        primary_respondent_id: backcheckConfig.primaryRespondentId,
        backcheck_respondent_id: backcheckConfig.backcheckRespondentId,
        backcheck_data_collector_id: backcheckConfig.backcheckDataCollectorId,
        backcheck_interview_date_id: backcheckConfig.backcheckInterviewDateId,
        type1_variables: backcheckConfig.type1Variables || [],
        type2_variables: backcheckConfig.type2Variables || [],
        type3_variables: backcheckConfig.type3Variables || [],
      };

      // =====================================================================
      // PHASE 2: Server-side backcheck when projectId is available
      // Sends only project_id + config — no raw data in request body
      // Falls back to v1 (data in body) when projectId is not provided
      // =====================================================================
      const useServerSide = !!projectId;
      
      const configPayload = {
        primary_respondent_id: analysisConfig.primary_respondent_id,
        backcheck_respondent_id: analysisConfig.backcheck_respondent_id,
        backcheck_data_collector_id: analysisConfig.backcheck_data_collector_id,
        backcheck_interview_date_id: analysisConfig.backcheck_interview_date_id,
        type1_variables: analysisConfig.type1_variables,
        type2_variables: analysisConfig.type2_variables,
        type3_variables: analysisConfig.type3_variables,
        exclude_missing: backcheckConfig.excludeMissing || false,
        exclude_numeric: excludeNumeric,
        exclude_string: excludeString,
        tolerance_ranges: toleranceRanges,
        string_lowercase: backcheckConfig.stringLowercase || false,
        string_trim: backcheckConfig.stringTrim || false,
        string_no_symbol: backcheckConfig.stringNoSymbol || false
      };

      let endpoint, requestBody, headers;
      
      if (useServerSide) {
        // V2: Reference data by project ID — no data transfer
        endpoint = `${API_BASE_URL}/run-backcheck-analysis-v2`;
        requestBody = {
          project_id: projectId,
          config: configPayload
        };
        headers = { 'Content-Type': 'application/json' };
        const token = localStorage.getItem('token');
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (userInfo?.email) headers['user_email'] = userInfo.email;
        console.log('Running backcheck analysis via V2 (server-side):', requestBody);
      } else {
        // V1 fallback: Send data in body
        endpoint = `${API_BASE_URL}/run-backcheck-analysis`;
        requestBody = {
          primary_data: primaryData?.full_data || primaryData?.data,
          backcheck_data: backcheckData?.full_data || backcheckData?.data,
          config: configPayload
        };
        headers = { 'Content-Type': 'application/json' };
        console.log('Running backcheck analysis via V1 (data in body)');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Request failed: ${errorText}`);
      }
      
      const results = await response.json();
      console.log('Backcheck analysis results:', results);
      
      setBackcheckResults(results);
      
      if (onNavigate) {
        onNavigate('backcheckdashboard');
      }

    } catch (error) {
      console.error('Error running backcheck analysis:', error);
      setBackcheckError?.(error.message || 'An error occurred while running back-check analysis');
    } finally {
      setIsLoadingBackcheck?.(false);
    }
  };

  const renderVariableSelectionSection = (type, title, description) => {
    const fieldName = `${type}Variables`;
    const selectedVariables = backcheckConfig[fieldName] || [];
    const isActive = expandedTypes.includes(type);
    const availableVariables = allVariables;
    
    return (
      <div 
        style={{
          border: isActive 
            ? (isDarkMode ? '10px solid #1F1F24' : '10px solid #eeeeeeff')
            : (isDarkMode ? '1px solid #292929ff' : '1px solid #dfdfdf'),
          borderRadius: '15px',
          padding: '1rem',
          background: isActive
            ? isDarkMode
              ? 'linear-gradient(135deg, #1F1F24 0%, #1F1F24 50%, #1F1F24 100%)'
              : 'linear-gradient(135deg, #eeeeeeff 0%, #eeeeeeff 50%, #ebebebff 100%)'
            : isDarkMode ? '#0D0E0E' : '#f5f5f5ff',
          marginBottom: '16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: isActive 
            ? (isDarkMode ? '0px 0px 0px 0px #000000ff' : '5px 7px 7px 5px #ffffffff')
            : (isDarkMode ? '0px 0px 0px #1D1D1D' : '0px 5px 5px 3px #F2F2F2')
        }}
        onClick={() => toggleVariableType(type)}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          marginBottom: isActive ? '0.5rem' : '0'
        }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '500',
            margin: 0
          }}>
            {title}
          </h3>
          
          {/* Select All / Clear Selection buttons - only show when card is expanded */}
          {isActive && (
            <div 
              style={{ 
                display: 'flex', 
                gap: '8px',
                marginLeft: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => selectAllVariables(type, e)}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: '500',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: isDarkMode ? '#3d3d3dff' : '#d5d5d5ff',
                  color: isDarkMode ? '#a0a0a0ff' : '#555555ff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = isDarkMode ? '#4d4d4dff' : '#c5c5c5ff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isDarkMode ? '#3d3d3dff' : '#d5d5d5ff';
                }}
              >
                Select All
              </button>
              <button
                onClick={(e) => clearVariableSelection(type, e)}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: '500',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: isDarkMode ? '#3d3d3dff' : '#d5d5d5ff',
                  color: isDarkMode ? '#a0a0a0ff' : '#555555ff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = isDarkMode ? '#4d4d4dff' : '#c5c5c5ff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isDarkMode ? '#3d3d3dff' : '#d5d5d5ff';
                }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
        
        <p style={{
          fontSize: '0.875rem',
          color: 'inherit',
          marginBottom: isActive ? '1rem' : '0',
          lineHeight: '1.4'
        }}>
          {description}
        </p>

        {isActive && (
          <div onClick={(e) => e.stopPropagation()}>
            {/* Search bar + count badge */}
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                marginTop: '0.5rem',
                marginBottom: '8px'
              }}
            >
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                borderRadius: '8px',
                backgroundColor: isDarkMode ? '#1a1a1e' : '#f0f0f0',
                border: isDarkMode ? '1px solid #2a2a2e' : '1px solid #e0e0e0',
              }}>
                <Search size={14} style={{ color: isDarkMode ? '#666' : '#999', flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Filter variables..."
                  value={variableSearch[type] || ''}
                  onChange={(e) => setVariableSearch(prev => ({ ...prev, [type]: e.target.value }))}
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '12px',
                    color: isDarkMode ? '#ccc' : '#333',
                  }}
                />
              </div>
              {selectedVariables.length > 0 && (
                <div style={{
                  padding: '4px 10px',
                  borderRadius: '12px',
                  backgroundColor: isDarkMode ? '#7c5b40' : '#e0a85f',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                  {selectedVariables.length} selected
                </div>
              )}
            </div>
          </div>
        )}

        {isActive && (
          <div 
            className="dropdown-scroller variableSelectionTab"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              marginTop: '0.25rem'
            }}
          >
            {availableVariables
              .filter(v => !variableSearch[type] || v.toLowerCase().includes((variableSearch[type] || '').toLowerCase()))
              .map(variable => {
              const isSelected = selectedVariables.includes(variable);
              const isDuplicate = isSelected && isDuplicateVariable(variable);
              return (
                <div
                  key={variable}
                  onMouseEnter={() => setHoveredVariable({ type, variable })}
                  onMouseLeave={() => setHoveredVariable({ type: null, variable: null })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem',
                    marginBottom: '0.25rem',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    userSelect: 'none',
                    backgroundColor: isDuplicate
                      ? (isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)')
                      : hoveredVariable.type === type && hoveredVariable.variable === variable
                        ? (isDarkMode ? '#2a2a2aff' : '#f5f5f5ff') 
                        : 'transparent',
                    border: isDuplicate ? '1px solid #EF4444' : '1px solid transparent',
                    transform: hoveredVariable.type === type && hoveredVariable.variable === variable
                      ? 'scale(1.01)'
                      : 'scale(1)',
                    transition: 'background-color 0.3s ease, transform 0.3s ease, border 0.3s ease'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVariableSelection(type, variable);
                  }}
                >
                  {/* Custom checkbox - matches ConfigureDetectors.js exactly */}
                  <div
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '4px',
                      border: isDarkMode ? '0px solid #4B5563' : '0px solid #D1D5DB',
                      backgroundColor: isDuplicate
                        ? '#EF4444'
                        : isSelected
                          ? (isDarkMode ? 'rgb(10, 197, 221)' : 'rgb(10, 197, 221)')
                          : (isDarkMode ? '#5e5e5eff' : '#FFFFFF'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M10 3L4.5 8.5L2 6"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span style={{ color: isDuplicate ? '#EF4444' : 'inherit' }}>{variable}</span>
                  <span style={{ fontSize: '0.875rem', color: isDuplicate ? '#EF4444' : (isDarkMode ? '#9CA3AF' : '#6B7280') }}>
                    ({columnTypes[variable]})
                  </span>
                  {isDuplicate && (
                    <span 
                      title="This variable is selected in multiple types"
                      style={{ 
                        marginLeft: 'auto', 
                        color: '#EF4444',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '0.75rem'
                      }}
                    >
                      <AlertTriangle size={14} />
                      Duplicate
                    </span>
                  )}
                </div>
              );
            })}
            {availableVariables.filter(v => !variableSearch[type] || v.toLowerCase().includes((variableSearch[type] || '').toLowerCase())).length === 0 && variableSearch[type] && (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                color: isDarkMode ? '#666' : '#999',
                fontSize: '13px',
              }}>
                No variables matching "{variableSearch[type]}"
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const hasAnySelections = 
    (backcheckConfig.type1Variables?.length > 0) ||
    (backcheckConfig.type2Variables?.length > 0) ||
    (backcheckConfig.type3Variables?.length > 0);

  const styles = {
    container: {
      minHeight: '100%', 
      width: '100%',
      maxWidth: '100%',
      padding: isMobile ? '24px 12px 24px 12px' : '40px 60px 24px 60px',
      backgroundColor: isDarkMode ? '#0D0E0E' : '#F5F5F599',
      // marginLeft: '70px',
    },
    errorMessage: {
      backgroundColor: isDarkMode ? '#7f1d1d33' : '#fee2e2',
      color: isDarkMode ? '#fca5a5' : '#991b1b',
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '13px',
      border: isDarkMode ? '1px solid #7f1d1d' : '1px solid #fca5a5'
    },
    runButton: {
      backgroundColor: isDarkMode ? 'rgb(10, 197, 221)' : 'rgb(10, 197, 221)', ///  '#4FC3F7aa' : '#4FC3F7aa', 
      color: isDarkMode ? '#000000' : '#000000',
      padding: '0.75rem 1.5rem',
      borderRadius: '40px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'background-color 0.2s ease',
      justifyContent: 'center',
      alignItems: 'center',
      display: 'flex',
      margin: '0 auto',
      marginBottom: '50px',
      fontSize: '13px'
    },
    disabledButton: {
      backgroundColor: '#585858ff',
      cursor: 'not-allowed'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: '1rem',
      marginBottom: '2rem'
    }
  };

  return (
    <>
      <AILoadingOverlay 
        isVisible={isLoadingBackcheck} 
        isDarkMode={isDarkMode}
        title="Running back-check analysis"
        subtitle="Comparing primary interviews against their verification re-interviews"
        tasks={[
          { id: 'type1', label: 'Type 1 comparisons', vars: backcheckConfig.type1Variables },
          { id: 'type2', label: 'Type 2 comparisons', vars: backcheckConfig.type2Variables },
          { id: 'type3', label: 'Type 3 comparisons', vars: backcheckConfig.type3Variables },
        ]
          .filter(t => (t.vars || []).length > 0)
          .map(t => ({
            id: t.id,
            label: t.label,
            detail: `${t.vars.length} variable${t.vars.length === 1 ? '' : 's'}`,
          }))
          .concat((backcheckConfig.toleranceRanges || []).filter(r => r.variable && (r.min || r.max)).length > 0
            ? [{ id: 'tolerance', label: 'Tolerance ranges', detail: `${(backcheckConfig.toleranceRanges || []).filter(r => r.variable && (r.min || r.max)).length} configured` }]
            : [])}
        realTimeStats={{
          totalRows: backcheckData?.total_rows || backcheckData?.full_data?.length || primaryData?.total_rows || primaryData?.full_data?.length || 0,
          columnsAnalyzed: backcheckData?.column_types 
            ? Object.keys(backcheckData.column_types).length 
            : (backcheckData?.columns?.length || 0),
        }}
        showDetailedLogs={true}
      />

      <div className='ConfigureBackChecksContainer' style={styles.container}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: '1s ease-in-out',
        }}>
          <h2 style={{fontSize: '22px', fontWeight: '500', display: 'flex', paddingRight: '20px'}}>
            Back-check Configuration
          </h2>
          
        </div>

        <div style={{
          maxHeight: isExpandedBackcheck ? '300vh' : '0px',
          transition: '0.3s ease-in-out',
          overflow: 'hidden',
          borderTop: isExpandedBackcheck 
            ? '0px solid #dfdfdf' 
            : (isDarkMode ? '1px solid #5c5c5c' : '1px solid #dfdfdfff'),
          borderBottom: isExpandedBackcheck 
            ? (isDarkMode ? '0px solid #5c5c5c' : '0px solid #dfdfdf') 
            : '0px solid #dfdfdf',
        }}>
          {backcheckError && (
            <div style={styles.errorMessage}>
              <AlertTriangle size={16} />
              {backcheckError}
            </div>
          )}

          {/* Real-time duplicate variable warning */}
          {duplicateVariables.length > 0 && !backcheckError && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '16px',
              backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)',
              border: '1px solid #EF4444',
              borderRadius: '12px',
              marginBottom: '24px',
              color: isDarkMode ? '#FCA5A5' : '#DC2626'
            }}>
              <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                  Duplicate Variables Detected
                </div>
                <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                  Each variable can only be assigned to one type. Please remove the following duplicates:
                </div>
                <ul style={{ 
                  margin: '8px 0 0 0', 
                  paddingLeft: '20px',
                  fontSize: '13px'
                }}>
                  {duplicateVariables.map(d => (
                    <li key={d.variable}>
                      <strong>{d.variable}</strong> — selected in {d.types.join(' and ')}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div style={{
            backgroundColor: isDarkMode ? '#141414ff' : '#f9fafbff',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '24px',
            border: isDarkMode ? '0px solid #292929ff' : '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px'
            }}>
              <Info size={20} color={isDarkMode ? '#6b6b6bff' : '#6B7280'} />
              <h3 style={{
                fontSize: '15px',
                fontWeight: '600',
                color: isDarkMode ? '#9e9e9eff' : '#374151',
                margin: 0
              }}>
                Back-check Analysis Configuration
              </h3>
            </div>
            <p style={{
              fontSize: '13px',
              color: isDarkMode ? '#6b6b6bff' : '#6B7280',
              lineHeight: '1.6',
              marginBottom: '0'
            }}>
              Configure back-check analysis to compare responses between primary survey data and back-check data. 
              Select the Respondent ID that will be used to match records between the two datasets. All variable 
              types are supported - numeric variables will use tolerance-based comparison, while string variables 
              will use exact matching.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            {/* Primary Respondent ID */}
            <div style={{marginLeft: '5px'}}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <CheckCircle2 size={20} />
                <label style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  marginLeft: '5px',
                  color: isDarkMode ? '#6b6b6bff' : '#374151'
                }}>
                  Primary Respondent ID <span style={{color: '#ef5844ff'}}>*</span>
                </label>
              </div>
              <select
                value={backcheckConfig.primaryRespondentId || ''}
                onChange={(e) => handleConfigChange('primaryRespondentId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  height: '35px',
                  borderRadius: '40rem',
                  border: isDarkMode ? '0px solid #6d6d6dff' : '0px solid #D1D5DB',
                  backgroundColor: isDarkMode ? '#0d0e0eff' : '#e9e9e9ff',
                  color: isDarkMode ? '#6b6b6bff' : '#6B7280',
                  fontSize: '13px',
                  cursor: 'pointer',
                  borderColor: !backcheckConfig.primaryRespondentId ? '#ef5844ff' : undefined
                }}
              >
                <option value="">Select Primary Respondent ID (Required)</option>
                {primaryColumns.map(variable => (
                  <option key={variable} value={variable}>
                    {variable}
                  </option>
                ))}
              </select>
              {!backcheckConfig.primaryRespondentId && (
                <div style={{
                  fontSize: '12px',
                  color: '#ef5844ff',
                  marginTop: '4px'
                }}>
                  Respondent ID is required for analysis
                </div>
              )}
            </div>

            {/* Backcheck Respondent ID */}
            <div style={{marginLeft: '5px'}}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <CheckCircle2 size={20} />
                <label style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  marginLeft: '5px',
                  color: isDarkMode ? '#6b6b6bff' : '#374151'
                }}>
                  Backcheck Respondent ID <span style={{color: '#ef5844ff'}}>*</span>
                </label>
              </div>
              <select
                value={backcheckConfig.backcheckRespondentId || ''}
                onChange={(e) => handleConfigChange('backcheckRespondentId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  height: '35px',
                  borderRadius: '40rem',
                  border: isDarkMode ? '0px solid #6d6d6dff' : '0px solid #D1D5DB',
                  backgroundColor: isDarkMode ? '#0d0e0eff' : '#e9e9e9ff',
                  color: isDarkMode ? '#6b6b6bff' : '#6B7280',
                  fontSize: '13px',
                  cursor: 'pointer',
                  borderColor: !backcheckConfig.backcheckRespondentId ? '#ef5844ff' : undefined
                }}
              >
                <option value="">Select Backcheck Respondent ID (Required)</option>
                {allVariables.map(variable => (
                  <option key={variable} value={variable}>
                    {variable}
                  </option>
                ))}
              </select>
              {!backcheckConfig.backcheckRespondentId && (
                <div style={{
                  fontSize: '12px',
                  color: '#ef5844ff',
                  marginTop: '4px'
                }}>
                  Usually the same column name as Primary
                </div>
              )}
            </div>

            {/* Back-check Data Collector ID */}
            <div style={{marginLeft: '5px'}}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
              }}>
                <Users size={20} />
                <label style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  marginLeft: '5px',
                  color: isDarkMode ? '#6b6b6bff' : '#374151'
                }}>
                  Back-check Data Collector ID <span style={{color: '#ef5844ff'}}>*</span>
                </label>
              </div>
              <select
                value={backcheckConfig.backcheckDataCollectorId || ''}
                onChange={(e) => handleConfigChange('backcheckDataCollectorId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  height: '35px',
                  borderRadius: '40rem',
                  border: isDarkMode ? '0px solid #6d6d6dff' : '0px solid #D1D5DB',
                  backgroundColor: isDarkMode ? '#0d0e0eff' : '#e9e9e9ff',
                  color: isDarkMode ? '#6b6b6bff' : '#6B7280',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <option value="">Select Back-check Data Collector ID</option>
                {allVariables.map(variable => (
                  <option key={variable} value={variable}>
                    {variable}
                  </option>
                ))}
              </select>
            </div>

            {/* Back-check Interview Date */}
            <div style={{marginLeft: '5px'}}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <Calendar size={20} />
                <label style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  marginLeft: '5px',
                  color: isDarkMode ? '#6b6b6bff' : '#374151'
                }}>
                  Back-check Interview Date <span style={{color: '#ef5844ff'}}>*</span>
                </label>
              </div>
              <select
                value={backcheckConfig.backcheckInterviewDateId || ''}
                onChange={(e) => handleConfigChange('backcheckInterviewDateId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  height: '35px',
                  borderRadius: '40rem',
                  border: isDarkMode ? '0px solid #6d6d6dff' : '0px solid #D1D5DB',
                  backgroundColor: isDarkMode ? '#0d0e0eff' : '#e9e9e9ff',
                  color: isDarkMode ? '#6b6b6bff' : '#6B7280',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <option value="">Select Back-check Interview Date</option>
                {getDateTimeVariables().length > 0 ? (
                  getDateTimeVariables().map(variable => {
                    const columnTypes = getBackcheckColumnTypes();
                    return (
                      <option key={variable} value={variable}>
                        {variable} ({columnTypes[variable]})
                      </option>
                    );
                  })
                ) : (
                  allVariables.map(variable => (
                    <option key={variable} value={variable}>
                      {variable}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
          
          {/* Variable Selection Sections */}
          {allVariables.length > 0 && (
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '16px',
                flexWrap: 'wrap'
              }}>
                <h3 style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: isDarkMode ? '#9e9e9eff' : '#374151',
                  margin: 0
                }}>
                  Variable Selection by Type (All Variable types supported)
                </h3>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={selectAllTypeCards}
                    disabled={expandedTypes.length === BACKCHECK_TYPES.length}
                    style={{
                      padding: '6px 16px',
                      borderRadius: '30px',
                      border: `1px solid ${isDarkMode ? '#3a3a3a' : '#d0d0d0'}`,
                      backgroundColor: 'transparent',
                      color: isDarkMode ? '#E0E0E0' : '#000000',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: expandedTypes.length === BACKCHECK_TYPES.length ? 'not-allowed' : 'pointer',
                      opacity: expandedTypes.length === BACKCHECK_TYPES.length ? 0.5 : 1,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={deselectAllTypeCards}
                    disabled={expandedTypes.length === 0}
                    style={{
                      padding: '6px 16px',
                      borderRadius: '30px',
                      border: `1px solid ${isDarkMode ? '#3a3a3a' : '#d0d0d0'}`,
                      backgroundColor: 'transparent',
                      color: isDarkMode ? '#E0E0E0' : '#000000',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: expandedTypes.length === 0 ? 'not-allowed' : 'pointer',
                      opacity: expandedTypes.length === 0 ? 0.5 : 1,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              <div style={styles.grid}>
                {renderVariableSelectionSection(
                  'type1',
                  'Type 1: Basic Demographic Variables',
                  'Type 1 variables are expected to stay constant between the survey and back check (e.g., age, household size, gender, location), and differences may result in action against the enumerator. Display variables with high error rates and complete enumerator checks.'
                )}

                {renderVariableSelectionSection(
                  'type2',
                  'Type 2: Complex Questions',
                  'Type 2 variables may be difficult for enumerators to administer (e.g., income amounts, expenditures, categorical responses).  For instance, they may involve complicated skip patterns or many examples.  Differences may indicate the need for further training, but will not result in action against the enumerator.  Display the error rates of all variables and complete enumerator and stability checks.'
                )}

                {renderVariableSelectionSection(
                  'type3',
                  'Type 3: Stability Analysis Variables',
                  'Type 3 variables are variables whose stability between the survey and back check is of interest (e.g., satisfaction ratings, yes/no responses, categorical data).  Differences will not result in action against the enumerator.  Display the error rates of all variables and complete stability checks.'
                )}
              </div>
            </div>
          )}

          {/* Advanced Options Section */}
          {hasAnySelections && (
            <div style={{
              marginTop: '24px',
              marginBottom: '24px',
              border: isDarkMode ? '1px solid #292929' : '1px solid #e5e7eb',
              borderRadius: '12px',
              overflow: 'hidden'
            }}>
              {/* Collapsible Header */}
              <div
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 20px',
                  backgroundColor: isDarkMode ? '#1a1a1a' : '#f9fafb',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Info size={18} color={isDarkMode ? '#6b7280' : '#6B7280'} />
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: isDarkMode ? '#e5e7eb' : '#374151'
                  }}>
                    Advanced Options
                  </span>
                  <span style={{
                    fontSize: '13px',
                    color: isDarkMode ? '#6b7280' : '#9ca3af'
                  }}>
                    (Exclusions, Tolerances, String Formatting)
                  </span>
                </div>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
                  strokeWidth="2"
                  style={{
                    transform: showAdvancedOptions ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>

              {/* Collapsible Content */}
              {showAdvancedOptions && (
                <div style={{
                  padding: '20px',
                  backgroundColor: isDarkMode ? '#141414' : '#ffffff',
                  borderTop: isDarkMode ? '1px solid #292929' : '1px solid #e5e7eb'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  {/* Exclude Missing Values */}
                  <div style={{ marginBottom: '24px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleConfigChange('excludeMissing', !backcheckConfig.excludeMissing)}
                    >
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        backgroundColor: backcheckConfig.excludeMissing
                          ? (isDarkMode ? '#e0a85f' : '#e0a85f')
                          : (isDarkMode ? '#3f3f46' : '#e5e7eb'),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}>
                        {backcheckConfig.excludeMissing && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <div>
                        <span style={{ fontWeight: '500', color: isDarkMode ? '#e5e7eb' : '#374151' }}>
                          Exclude Missing Values
                        </span>
                        <p style={{ fontSize: '13px', color: isDarkMode ? '#6b7280' : '#9ca3af', margin: '4px 0 0 0' }}>
                          Don't compare responses where the back-check value is missing
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Exclude Values Section */}
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    backgroundColor: isDarkMode ? '#1f1f23' : '#f9fafb',
                    borderRadius: '8px'
                  }}>
                    <h4 style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: isDarkMode ? '#e5e7eb' : '#374151',
                      marginBottom: '12px'
                    }}>
                      Exclude Values from Comparison
                    </h4>
                    <p style={{ fontSize: '13px', color: isDarkMode ? '#6b7280' : '#9ca3af', marginBottom: '16px' }}>
                      Skip comparison when back-check response equals these values (e.g., "Don't Know" or "Refused" codes)
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                      {/* Numeric Exclusions */}
                      <div>
                        <label style={{
                          fontSize: '13px',
                          fontWeight: '500',
                          color: isDarkMode ? '#9ca3af' : '#6b7280',
                          display: 'block',
                          marginBottom: '6px'
                        }}>
                          Numeric Values (comma-separated)
                        </label>
                        <input
                          type="text"
                          placeholder="-999, -888, 999"
                          value={backcheckConfig.excludeNumeric || ''}
                          onChange={(e) => handleConfigChange('excludeNumeric', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '6px',
                            border: isDarkMode ? '1px solid #3f3f46' : '1px solid #d1d5db',
                            backgroundColor: isDarkMode ? '#27272a' : '#ffffff',
                            color: isDarkMode ? '#e5e7eb' : '#374151',
                            fontSize: '13px'
                          }}
                        />
                      </div>

                      {/* String Exclusions */}
                      <div>
                        <label style={{
                          fontSize: '13px',
                          fontWeight: '500',
                          color: isDarkMode ? '#9ca3af' : '#6b7280',
                          display: 'block',
                          marginBottom: '6px'
                        }}>
                          String Values (comma-separated)
                        </label>
                        <input
                          type="text"
                          placeholder="DK, NA, Refused"
                          value={backcheckConfig.excludeString || ''}
                          onChange={(e) => handleConfigChange('excludeString', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '6px',
                            border: isDarkMode ? '1px solid #3f3f46' : '1px solid #d1d5db',
                            backgroundColor: isDarkMode ? '#27272a' : '#ffffff',
                            color: isDarkMode ? '#e5e7eb' : '#374151',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tolerance Ranges (okrange) Section */}
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    backgroundColor: isDarkMode ? '#1f1f23' : '#f9fafb',
                    borderRadius: '8px'
                  }}>
                    <h4 style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: isDarkMode ? '#e5e7eb' : '#374151',
                      marginBottom: '12px'
                    }}>
                      Tolerance Ranges (Acceptable Difference Bounds)
                    </h4>
                    <p style={{ fontSize: '13px', color: isDarkMode ? '#6b7280' : '#9ca3af', marginBottom: '16px' }}>
                      Don't count as a difference if back-check value falls within this range of the survey value. 
                      Use absolute values (e.g., -1, 1) or percentages (e.g., -5%, 5%).
                    </p>

                    {/* Tolerance Table */}
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ backgroundColor: isDarkMode ? '#27272a' : '#f3f4f6' }}>
                            <th style={{ padding: '10px 12px', textAlign: 'left', color: isDarkMode ? '#9ca3af' : '#6b7280', fontWeight: '500' }}>Variable</th>
                            <th style={{ padding: '10px 12px', textAlign: 'center', color: isDarkMode ? '#9ca3af' : '#6b7280', fontWeight: '500', width: '120px' }}>Min Tolerance</th>
                            <th style={{ padding: '10px 12px', textAlign: 'center', color: isDarkMode ? '#9ca3af' : '#6b7280', fontWeight: '500', width: '120px' }}>Max Tolerance</th>
                            <th style={{ padding: '10px 12px', textAlign: 'center', width: '60px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(backcheckConfig.toleranceRanges || []).map((range, index) => (
                            <tr key={index} style={{ borderBottom: isDarkMode ? '1px solid #3f3f46' : '1px solid #e5e7eb' }}>
                              <td style={{ padding: '8px 12px' }}>
                                <select
                                  value={range.variable || ''}
                                  onChange={(e) => {
                                    const newRanges = [...(backcheckConfig.toleranceRanges || [])];
                                    newRanges[index] = { ...newRanges[index], variable: e.target.value };
                                    handleConfigChange('toleranceRanges', newRanges);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: isDarkMode ? '1px solid #3f3f46' : '1px solid #d1d5db',
                                    backgroundColor: isDarkMode ? '#27272a' : '#ffffff',
                                    color: isDarkMode ? '#e5e7eb' : '#374151',
                                    fontSize: '13px'
                                  }}
                                >
                                  <option value="">Select variable</option>
                                  {[...(backcheckConfig.type1Variables || []), ...(backcheckConfig.type2Variables || []), ...(backcheckConfig.type3Variables || [])].map(v => (
                                    <option key={v} value={v}>{v}</option>
                                  ))}
                                </select>
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                <input
                                  type="text"
                                  placeholder="-1 or -5%"
                                  value={range.min || ''}
                                  onChange={(e) => {
                                    const newRanges = [...(backcheckConfig.toleranceRanges || [])];
                                    newRanges[index] = { ...newRanges[index], min: e.target.value };
                                    handleConfigChange('toleranceRanges', newRanges);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: isDarkMode ? '1px solid #3f3f46' : '1px solid #d1d5db',
                                    backgroundColor: isDarkMode ? '#27272a' : '#ffffff',
                                    color: isDarkMode ? '#e5e7eb' : '#374151',
                                    fontSize: '13px',
                                    textAlign: 'center'
                                  }}
                                />
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                <input
                                  type="text"
                                  placeholder="1 or 5%"
                                  value={range.max || ''}
                                  onChange={(e) => {
                                    const newRanges = [...(backcheckConfig.toleranceRanges || [])];
                                    newRanges[index] = { ...newRanges[index], max: e.target.value };
                                    handleConfigChange('toleranceRanges', newRanges);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: isDarkMode ? '1px solid #3f3f46' : '1px solid #d1d5db',
                                    backgroundColor: isDarkMode ? '#27272a' : '#ffffff',
                                    color: isDarkMode ? '#e5e7eb' : '#374151',
                                    fontSize: '13px',
                                    textAlign: 'center'
                                  }}
                                />
                              </td>
                              <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                <button
                                  onClick={() => {
                                    const newRanges = (backcheckConfig.toleranceRanges || []).filter((_, i) => i !== index);
                                    handleConfigChange('toleranceRanges', newRanges);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#ef4444',
                                    cursor: 'pointer',
                                    padding: '4px'
                                  }}
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <button
                      onClick={() => {
                        const newRanges = [...(backcheckConfig.toleranceRanges || []), { variable: '', min: '', max: '' }];
                        handleConfigChange('toleranceRanges', newRanges);
                      }}
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: isDarkMode ? '1px solid #3f3f46' : '1px solid #d1d5db',
                        backgroundColor: 'transparent',
                        color: isDarkMode ? '#e0a85f' : '#0284c7',
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      + Add Tolerance Range
                    </button>
                  </div>

                  {/* String Comparison Options */}
                  <div style={{
                    padding: '16px',
                    backgroundColor: isDarkMode ? '#1f1f23' : '#f9fafb',
                    borderRadius: '8px'
                  }}>
                    <h4 style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: isDarkMode ? '#e5e7eb' : '#374151',
                      marginBottom: '12px'
                    }}>
                      String Comparison Options
                    </h4>
                    <p style={{ fontSize: '13px', color: isDarkMode ? '#6b7280' : '#9ca3af', marginBottom: '16px' }}>
                      Normalize string values before comparing to reduce false differences
                    </p>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                      {/* Convert to Lowercase */}
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                        onClick={() => handleConfigChange('stringLowercase', !backcheckConfig.stringLowercase)}
                      >
                        <div style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '4px',
                          backgroundColor: backcheckConfig.stringLowercase
                            ? (isDarkMode ? '#e0a85f' : '#e0a85f')
                            : (isDarkMode ? '#3f3f46' : '#e5e7eb'),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {backcheckConfig.stringLowercase && (
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                              <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <span style={{ fontSize: '13px', color: isDarkMode ? '#e5e7eb' : '#374151' }}>
                          Convert to lowercase
                        </span>
                      </div>

                      {/* Trim Whitespace */}
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                        onClick={() => handleConfigChange('stringTrim', !backcheckConfig.stringTrim)}
                      >
                        <div style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '4px',
                          backgroundColor: backcheckConfig.stringTrim
                            ? (isDarkMode ? '#e0a85f' : '#e0a85f')
                            : (isDarkMode ? '#3f3f46' : '#e5e7eb'),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {backcheckConfig.stringTrim && (
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                              <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <span style={{ fontSize: '13px', color: isDarkMode ? '#e5e7eb' : '#374151' }}>
                          Trim whitespace
                        </span>
                      </div>

                      {/* Remove Symbols */}
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                        onClick={() => handleConfigChange('stringNoSymbol', !backcheckConfig.stringNoSymbol)}
                      >
                        <div style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '4px',
                          backgroundColor: backcheckConfig.stringNoSymbol
                            ? (isDarkMode ? '#e0a85f' : '#e0a85f')
                            : (isDarkMode ? '#3f3f46' : '#e5e7eb'),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {backcheckConfig.stringNoSymbol && (
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                              <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                        <span style={{ fontSize: '13px', color: isDarkMode ? '#e5e7eb' : '#374151' }}>
                          Remove symbols
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Run Analysis Button */}
          {hasAnySelections && (
            <div style={{
              textAlign: 'center',
              marginTop: '24px'
            }}>
              <button
                onClick={runBackcheckAnalysis}
                disabled={isLoadingBackcheck || allVariables.length === 0 || duplicateVariables.length > 0}
                // className={duplicateVariables.length === 0 ? 'liquid-glass-button' : ''}
                style={{
                  ...styles.runButton,
                  ...((isLoadingBackcheck || allVariables.length === 0 || duplicateVariables.length > 0) ? styles.disabledButton : {}),
                  ...(duplicateVariables.length > 0 ? {
                    backgroundColor: isDarkMode ?  '#4FC3F7aa' : '#4FC3F7aa',
                    cursor: 'not-allowed',
                    opacity: 0.6
                  } : {})
                }}
              >
                {isLoadingBackcheck 
                  ? 'Running Analysis...' 
                  : duplicateVariables.length > 0 
                    ? `Fix ${duplicateVariables.length} Duplicate${duplicateVariables.length > 1 ? 's' : ''} to Continue`
                    : 'Run Back-check Analysis'
                }
              </button>
              {duplicateVariables.length > 0 && (
                <p style={{
                  marginTop: '12px',
                  fontSize: '13px',
                  color: '#EF4444'
                }}>
                  Remove duplicate variables before running analysis
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ConfigureBackChecks;